import React, { useState } from 'react';
import { PlusOutlined, CloseOutlined, CheckOutlined } from '@ant-design/icons';
import type { OfertaRequest } from '../../../../store/MSCommerce/Ofertas/types';
import type { ProductosResponse } from '../../../../store/MSCommerce/Productos/types';
import type { CategoryResponse } from '../../../../store/MSCommerce/Categories/types';
import styles from '../Ofertas.module.scss';

interface CreateOfertaModalProps {
  onClose: () => void;
  onSubmit: (data: OfertaRequest) => void;
  productos?: ProductosResponse[];
  categorias?: CategoryResponse[];
}

export const CreateOfertaModal: React.FC<CreateOfertaModalProps> = ({
  onClose,
  onSubmit,
  productos,
  categorias,
}) => {
  const [form, setForm] = useState({
    nombre: '',
    tipo: 'PORCENTAJE',
    valor: '',
    fechaInicio: '',
    fechaFin: '',
    aplicaA: 'PRODUCTO',
  });
  const [seleccionados, setSeleccionados] = useState<string[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const field = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (key === 'aplicaA') { setSeleccionados([]); setBusqueda(''); }
  };

  const toggleSeleccion = (id: string) => {
    setSeleccionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.nombre.trim()) e.nombre = 'El nombre es requerido';
    if (form.tipo !== '2x1' && (!form.valor || isNaN(Number(form.valor)) || Number(form.valor) <= 0)) {
      e.valor = 'Ingresa un valor válido';
    }
    if (!form.fechaInicio) e.fechaInicio = 'Requerida';
    if (!form.fechaFin) e.fechaFin = 'Requerida';
    if (form.fechaInicio && form.fechaFin && form.fechaFin < form.fechaInicio) {
      e.fechaFin = 'Debe ser posterior a la fecha de inicio';
    }
    if (seleccionados.length === 0) e.seleccionados = 'Elige al menos uno';
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    onSubmit({
      nombre: form.nombre,
      tipo: form.tipo,
      valor: form.tipo === '2x1' ? 0 : Number(form.valor),
      fechaInicio: new Date(form.fechaInicio),
      fechaFin: new Date(form.fechaFin),
      aplicaA: form.aplicaA,
      detalles: seleccionados.map(id => ({ referenciaId: id, tipoReferencia: form.aplicaA })),
    });
  };

  const opciones: { id: string; nombre: string }[] =
    form.aplicaA === 'CATEGORIA' ? (categorias ?? []) : (productos ?? []);
  const opcionesFiltradas = opciones.filter(op =>
    op.nombre.toLowerCase().includes(busqueda.toLowerCase())
  );
  const nombreSeleccionado = (id: string) => opciones.find(op => op.id === id)?.nombre ?? id;

  return (
    <div className={styles.modal__overlay} onClick={onClose}>
      <div className={styles.modal__panel} onClick={e => e.stopPropagation()}>

        <div className={styles.modal__header}>
          <div className={styles.modal__headerLeft}>
            <h2 className={styles.modal__title}>Nueva Oferta</h2>
          </div>
          <button className={styles.modal__close} onClick={onClose}><CloseOutlined /></button>
        </div>

        <div className={styles.modal__body}>
          <div className={styles.modal__grid}>

            <div className={`${styles.modal__field} ${styles['modal__field--full']}`}>
              <label className={styles.modal__label}>Nombre de la oferta *</label>
              <input
                className={`${styles.modal__input} ${errors.nombre ? styles['modal__input--error'] : ''}`}
                placeholder="Ej: Rebajas de verano"
                value={form.nombre}
                onChange={e => field('nombre', e.target.value)}
              />
              {errors.nombre && <span className={styles.modal__error}>{errors.nombre}</span>}
            </div>

            <div className={styles.modal__field}>
              <label className={styles.modal__label}>Tipo *</label>
              <div className={styles.modal__statusGroup}>
                {['PORCENTAJE', 'MONTO_FIJO', '2x1'].map(t => (
                  <button
                    key={t}
                    type="button"
                    className={`${styles.modal__statusBtn} ${form.tipo === t ? styles['modal__statusBtn--active'] : ''}`}
                    onClick={() => field('tipo', t)}
                  >
                    {t === 'PORCENTAJE' ? '% Porcentaje' : t === 'MONTO_FIJO' ? '$ Monto fijo' : '2x1'}
                  </button>
                ))}
              </div>
            </div>

            {form.tipo !== '2x1' && (
              <div className={styles.modal__field}>
                <label className={styles.modal__label}>
                  Valor * {form.tipo === 'PORCENTAJE' ? '(%)' : '($)'}
                </label>
                <input
                  className={`${styles.modal__input} ${errors.valor ? styles['modal__input--error'] : ''}`}
                  type="number"
                  min="0"
                  placeholder={form.tipo === 'PORCENTAJE' ? '20' : '5.00'}
                  value={form.valor}
                  onChange={e => field('valor', e.target.value)}
                />
                {errors.valor && <span className={styles.modal__error}>{errors.valor}</span>}
              </div>
            )}

            <div className={styles.modal__field}>
              <label className={styles.modal__label}>Fecha de inicio *</label>
              <input
                className={`${styles.modal__input} ${errors.fechaInicio ? styles['modal__input--error'] : ''}`}
                type="date"
                value={form.fechaInicio}
                onChange={e => field('fechaInicio', e.target.value)}
              />
              {errors.fechaInicio && <span className={styles.modal__error}>{errors.fechaInicio}</span>}
            </div>

            <div className={styles.modal__field}>
              <label className={styles.modal__label}>Fecha de fin *</label>
              <input
                className={`${styles.modal__input} ${errors.fechaFin ? styles['modal__input--error'] : ''}`}
                type="date"
                value={form.fechaFin}
                onChange={e => field('fechaFin', e.target.value)}
              />
              {errors.fechaFin && <span className={styles.modal__error}>{errors.fechaFin}</span>}
            </div>

            <div className={styles.modal__field}>
              <label className={styles.modal__label}>Aplica a *</label>
              <div className={styles.modal__statusGroup}>
                <button
                  type="button"
                  className={`${styles.modal__statusBtn} ${form.aplicaA === 'PRODUCTO' ? styles['modal__statusBtn--active'] : ''}`}
                  onClick={() => field('aplicaA', 'PRODUCTO')}
                >
                  Productos
                </button>
                <button
                  type="button"
                  className={`${styles.modal__statusBtn} ${form.aplicaA === 'CATEGORIA' ? styles['modal__statusBtn--active'] : ''}`}
                  onClick={() => field('aplicaA', 'CATEGORIA')}
                >
                  Categorías
                </button>
              </div>
            </div>

            <div className={`${styles.modal__field} ${styles['modal__field--full']}`}>
              <label className={styles.modal__label}>
                {form.aplicaA === 'CATEGORIA' ? 'Categorías incluidas *' : 'Productos incluidos *'}
              </label>

              {seleccionados.length > 0 && (
                <div className={styles.picker__chips}>
                  {seleccionados.map(id => (
                    <span key={id} className={styles.picker__chip}>
                      {nombreSeleccionado(id)}
                      <button type="button" onClick={() => toggleSeleccion(id)}>
                        <CloseOutlined />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <input
                className={styles.modal__input}
                placeholder={form.aplicaA === 'CATEGORIA' ? 'Busca categorías...' : 'Busca productos...'}
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
              />

              <div className={styles.picker__list}>
                {opcionesFiltradas.length > 0 ? (
                  opcionesFiltradas.map(op => (
                    <div
                      key={op.id}
                      className={`${styles.picker__item} ${seleccionados.includes(op.id) ? styles['picker__item--selected'] : ''}`}
                      onClick={() => toggleSeleccion(op.id)}
                    >
                      <span className={styles.picker__itemCheck}>
                        {seleccionados.includes(op.id) && <CheckOutlined />}
                      </span>
                      {op.nombre}
                    </div>
                  ))
                ) : (
                  <div className={styles.picker__empty}>Sin resultados</div>
                )}
              </div>

              {errors.seleccionados && <span className={styles.modal__error}>{errors.seleccionados}</span>}
            </div>

          </div>
        </div>

        <div className={styles.modal__footer}>
          <button className={styles.modal__cancel} onClick={onClose}>Cancelar</button>
          <button className={styles.modal__submit} onClick={handleSubmit}>
            <PlusOutlined /> Crear Oferta
          </button>
        </div>

      </div>
    </div>
  );
};
